import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { translate } from "../../../hooks/translate";
import { useTitle } from "../../../hooks/useTitle";
import { formatDateTimeTrInAppTz } from "../../../utils/appTime";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Input from "../../../components/BaseUI/Input";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  FiServer,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiUser,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import "./MachineAssignments.scss";
import { withAuth } from "../../../components/HOC/withAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const MachineAssignments = () => {
  useTitle("Rovion MES - Makine Atamaları");
  const { getToken, getUser } = useAuth();
  const navigate = useNavigate();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/machine-assignments",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [toasts, setToasts] = useState([]);
  const [expandedCards, setExpandedCards] = useState({});

  const headers = [
    { name: "Atama Adı", dataType: "text", id: 0 },
    { name: "Kullanıcılar", dataType: "text", id: 1 },
    { name: "Makineler", dataType: "text", id: 2 },
    { name: "Oluşturulma", dataType: "date", id: 3 },
    { name: "İşlemler", id: 4 },
  ];

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("machine-assignments-list", headers);

  const addToast = (message, type = "info") => {
    const newToast = {
      id: Date.now(),
      message,
      type,
    };
    setToasts([...toasts, newToast]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== newToast.id));
    }, 3000);
  };

  useEffect(() => {
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/machine-assignments`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Backend returns { success: true, data: { assignments: [...] } }
      const assignmentsData =
        response.data?.data?.assignments ||
        response.data?.assignments ||
        response.data;
      setAssignments(Array.isArray(assignmentsData) ? assignmentsData : []);
    } catch (error) {
      console.error("Failed to fetch assignments:", error);
      addToast("Makine atamaları yüklenemedi", "danger");
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (assignmentId) => {
    if (!window.confirm("Bu atamayı silmek istediğinizden emin misiniz?")) {
      return;
    }

    try {
      const token = getToken();
      await axios.delete(`${API_URL}/api/machine-assignments/${assignmentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      addToast("Atama başarıyla silindi", "success");
      fetchAssignments();
    } catch (error) {
      console.error("Failed to delete assignment:", error);
      addToast(error.response?.data?.message || "Atama silinemedi", "danger");
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    return formatDateTimeTrInAppTz(dateString);
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: "Aktif",
      completed: "Tamamlandı",
      cancelled: "İptal Edildi",
    };
    return labels[status] || status;
  };

  const getStatusVariant = (status) => {
    const variants = {
      active: "success",
      completed: "info",
      cancelled: "danger",
    };
    return variants[status] || "default";
  };

  const filteredAssignments = (assignments || []).filter((assignment) => {
    const query = searchQuery.toLowerCase();
    // Backend returns arrays of users and machines, so check the first items
    const userName = assignment?.userIds?.[0]?.name || "";
    const machineName = assignment?.machineIds?.[0]?.name || "";
    const machineCode = assignment?.machineIds?.[0]?.code || "";
    const assignmentName = assignment?.assignmentName || "";

    return (
      userName.toLowerCase().includes(query) ||
      machineName.toLowerCase().includes(query) ||
      machineCode.toLowerCase().includes(query) ||
      assignmentName.toLowerCase().includes(query)
    );
  });

  const toggleCard = (assignmentId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [assignmentId]: !prev[assignmentId],
    }));
  };

  return (
    <AppPage
      title="Makine Atamaları"
      subtitle={`${filteredAssignments.length} Atama`}
      icon={<FiServer />}
      actions={
        canWrite
          ? [
              <Button
                appearance="primary-animated"
                onClick={() => navigate("/machine-assignments/create")}
                icon={<FiPlus />}
              >
                Yeni Atama
              </Button>,
            ]
          : []
      }
    >
      <CardContainer>
        {loading ? (
          <div className="loading-container">
            <FiServer className="spin" size={48} />
            <p>Yükleniyor...</p>
          </div>
        ) : (
          <>
            {!filteredAssignments || filteredAssignments.length === 0 ? (
              <div className="empty-state">
                <FiServer size={64} />
                <p>Atama bulunamadı</p>
              </div>
            ) : (
              <div className="assignments-grid">
                {filteredAssignments.map((assignment) => (
                  <div key={assignment._id} className="assignment-card">
                    <div className="assignment-card__header">
                      <div className="assignment-card__avatar">
                        <FiServer size={24} />
                      </div>
                      <div className="assignment-card__title">
                        <h3>{assignment.assignmentName || "İsimsiz Atama"}</h3>
                        <p>{formatDateTime(assignment.createdAt)}</p>
                      </div>
                      <div className="assignment-card__actions">
                        {canWrite && (
                          <>
                            <IconButton
                              icon={<FiEdit2 />}
                              appearance="subtle"
                              shape="circle"
                              onClick={() =>
                                navigate(
                                  `/machine-assignments/edit/${assignment._id}`,
                                )
                              }
                            />
                            <IconButton
                              icon={<FiTrash2 />}
                              appearance="subtle"
                              shape="circle"
                              onClick={() => handleDelete(assignment._id)}
                            />
                          </>
                        )}
                      </div>
                    </div>

                    <div className="assignment-card__section">
                      <div className="assignment-card__section-header">
                        <span>Kullanıcı Listesi</span>
                        {assignment.userIds &&
                          assignment.userIds.length > 0 && (
                            <button
                              className="collapse-button"
                              onClick={() =>
                                toggleCard(`${assignment._id}-users`)
                              }
                            >
                              {expandedCards[`${assignment._id}-users`] ? (
                                <FiChevronUp size={18} />
                              ) : (
                                <FiChevronDown size={18} />
                              )}
                            </button>
                          )}
                      </div>
                      <div
                        className={`assignment-card__list ${
                          expandedCards[`${assignment._id}-users`]
                            ? "expanded"
                            : "collapsed"
                        }`}
                      >
                        {assignment.userIds && assignment.userIds.length > 0 ? (
                          assignment.userIds.map((user, index) => (
                            <div
                              key={user._id || index}
                              className="assignment-card__list-item"
                            >
                              <FiUser size={16} />
                              <div className="list-item-content">
                                <strong>{user?.name || "-"}</strong>
                                <span className="text-muted">
                                  {user?.email || "-"}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="assignment-card__empty">
                            Kullanıcı atanmamış
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="assignment-card__section">
                      <div className="assignment-card__section-header">
                        <span>Makine Listesi</span>
                        {assignment.machineIds &&
                          assignment.machineIds.length > 0 && (
                            <button
                              className="collapse-button"
                              onClick={() =>
                                toggleCard(`${assignment._id}-machines`)
                              }
                            >
                              {expandedCards[`${assignment._id}-machines`] ? (
                                <FiChevronUp size={18} />
                              ) : (
                                <FiChevronDown size={18} />
                              )}
                            </button>
                          )}
                      </div>
                      <div
                        className={`assignment-card__list ${
                          expandedCards[`${assignment._id}-machines`]
                            ? "expanded"
                            : "collapsed"
                        }`}
                      >
                        {assignment.machineIds &&
                        assignment.machineIds.length > 0 ? (
                          assignment.machineIds.map((machine, index) => (
                            <div
                              key={machine._id || index}
                              className="assignment-card__list-item"
                            >
                              <FiServer size={16} />
                              <div className="list-item-content">
                                <strong>{machine?.name || "-"}</strong>
                                <span className="text-muted">
                                  Kod: {machine?.code || "-"}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="assignment-card__empty">
                            Makine atanmamış
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContainer>

      <ToastContainer>
        {(toasts || []).map((toast) => (
          <Toast
            key={toast.id}
            title={toast.message}
            appearance={toast.type}
            onClose={() =>
              setToasts((toasts || []).filter((t) => t.id !== toast.id))
            }
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(MachineAssignments, ["admin", "employee", "user"]);
